import { useState, useCallback, useRef } from 'react';
import { ApiError } from '../types/api';

interface UseApiOptions<T> {
  onSuccess?: (data: T, args?: any[]) => void;
  onError?: (error: ApiError) => void;
  initialData?: T;
  retryCount?: number;
  retryDelay?: number;
}

export function useApi<T>(
  apiCall: (...args: any[]) => Promise<T>,
  options: UseApiOptions<T> = {}
) {
  const {
    onSuccess,
    onError,
    initialData,
    retryCount = 3,
    retryDelay = 1000
  } = options;

  const [data, setData] = useState<T | undefined>(initialData);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const execute = useCallback(async (...args: any[]) => {
    let attempts = 0;
    
    // Cancel any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const executeWithRetry = async (): Promise<T> => {
      try {
        setLoading(true);
        setError(null);
        
        const result = await apiCall(...args);
        setData(result);
        onSuccess?.(result, args);
        return result;
      } catch (err) {
        const apiError = err as ApiError;
        
        // Retry on network errors or 5xx server errors
        if (
          attempts < retryCount &&
          (apiError.code === 'NETWORK_ERROR' || apiError.status >= 500)
        ) {
          attempts++;
          await new Promise(resolve => setTimeout(resolve, retryDelay * attempts));
          return executeWithRetry();
        }

        setError(apiError);
        onError?.(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    };

    return executeWithRetry();
  }, [apiCall, onSuccess, onError, retryCount, retryDelay]);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setLoading(false);
    }
  }, []);

  return {
    data,
    error,
    loading,
    execute,
    cancel,
    setData,
    setError
  };
}